import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Plus, Edit, Trash2, Save, LogOut, Users, Key, Shield, User } from 'lucide-react';
import { toast } from 'sonner';
import { api, adminAuthHeaders, adminMultipartHeaders, BACKEND_URL } from '../lib/api';
import { clearAdminToken } from '../lib/session';
import { GalleryInput, ImageInput } from '../components/admin/MediaInputs';
import { DesignControlsEditor, SettingsEditor, ThemeEditor } from '../components/admin/SettingsEditors';
import { FormEditor } from '../components/admin/FormEditor';
import { EditForm } from '../components/admin/EditForm';
import { DynamicPageEditor } from '../components/admin/DynamicPageEditor';
import { AdminWorkflowPanel } from '../components/admin/AdminWorkflowPanel';
import { isValidSlug } from '../components/admin/adminValidation';
import { useAdminResource } from '../hooks/useAdminResource';

function validateContentItem(item, type) {
  if (type !== 'team' && type !== 'pages') {
    if (!item.slug?.trim()) return 'Slug is required';
    if (!isValidSlug(item.slug)) return 'Slug must use lowercase kebab-case';
  }

  if (type === 'pages') {
    if (!item.slug?.trim()) return 'Page slug is required';
    if (!item.title?.trim()) return 'Page title is required';
    if (!item.content?.trim()) return 'Page content is required';
    return null;
  }

  if (type === 'team') {
    if (!item.name?.trim()) return 'Team member name is required';
    if (!item.position?.trim()) return 'Team member position is required';
    return null;
  }

  if (type === 'articles') {
    if (!item.title?.trim()) return 'Article title is required';
    if (!item.excerpt?.trim()) return 'Article excerpt is required';
    return null;
  }

  if (type === 'cases' || type === 'events') {
    if (!item.title?.trim()) return 'Title is required';
    if (!item.description?.trim()) return 'Description is required';
    return null;
  }

  if (!item.name?.trim()) return 'Name is required';
  if (!item.description?.trim()) return 'Description is required';
  return null;
}

function validateDynamicPage(page) {
  if (!page.slug?.trim()) return 'Page slug is required';
  if (!isValidSlug(page.slug)) return 'Page slug must use lowercase kebab-case';
  if (!page.title?.trim()) return 'Page title is required';
  if (!Array.isArray(page.blocks) || page.blocks.length === 0) return 'Add at least one content block';
  return null;
}

function validateFormDefinition(form) {
  if (!form.slug?.trim()) return 'Form slug is required';
  if (!isValidSlug(form.slug)) return 'Form slug must use lowercase kebab-case';
  if (!form.title?.trim()) return 'Form title is required';
  if (!Array.isArray(form.fields) || form.fields.length === 0) return 'Add at least one form field';

  for (const field of form.fields) {
    if (!field.label?.trim()) return 'Each field must have a label';
    if (!field.type?.trim()) return 'Each field must have a type';
    if (field.type === 'select' && (!Array.isArray(field.options) || field.options.length === 0)) {
      return 'Select fields must include at least one option';
    }
  }

  return null;
}

export default function Admin() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('services');
  const [data, setData] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [userRole, setUserRole] = useState('admin');
  const [currentUsername, setCurrentUsername] = useState('');
  
  // Password change state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({ current_password: '', new_password: '', confirm_password: '' });
  
  // User management state
  const [editingUser, setEditingUser] = useState(null);
  const [newUserData, setNewUserData] = useState({ username: '', password: '', role: 'admin' });
  const [resetPasswordUserId, setResetPasswordUserId] = useState(null);
  const [resetPasswordValue, setResetPasswordValue] = useState('');
  const [confirmState, setConfirmState] = useState({ open: false, title: '', description: '', action: null });

  // Tours state
  const [editingTour, setEditingTour] = useState(null);

  const [editingPage, setEditingPage] = useState(null);
  const [isCreatingPage, setIsCreatingPage] = useState(false);

  const [editingForm, setEditingForm] = useState(null);
  const [isCreatingForm, setIsCreatingForm] = useState(false);

  // Submissions state
  const [submissions, setSubmissions] = useState([]);
  const [selectedFormId, setSelectedFormId] = useState('all');

  // Media state
  const [mediaFile, setMediaFile] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortMode, setSortMode] = useState('title-asc');

  const {
    data: users,
    setData: setUsers,
    refresh: fetchUsers,
  } = useAdminResource('/admin/users', {
    enabled: activeTab === 'users',
    initialData: [],
    onError: (error) => {
      if (error.response?.status === 403) {
        toast.error('Нет доступа к управлению пользователями');
      }
    },
  });

  const {
    data: siteSettings,
    setData: setSiteSettings,
    refresh: fetchSiteSettings,
  } = useAdminResource('/admin/settings', {
    enabled: activeTab === 'navigation' || activeTab === 'theme' || activeTab === 'design',
    initialData: null,
    onError: () => {
      toast.error('Failed to load site settings');
    },
  });

  const {
    data: dynamicPages,
    setData: setDynamicPages,
    refresh: fetchDynamicPages,
  } = useAdminResource('/admin/pages-dynamic', {
    enabled: activeTab === 'site_pages' || activeTab === 'tours',
    initialData: [],
    onError: () => {
      toast.error('Failed to load pages');
    },
  });

  const {
    data: forms,
    setData: setForms,
    refresh: fetchForms,
  } = useAdminResource('/admin/forms', {
    enabled: activeTab === 'forms' || activeTab === 'submissions',
    initialData: [],
    onError: () => {
      toast.error('Failed to load forms');
    },
  });

  const {
    data: mediaItems,
    setData: setMediaItems,
    refresh: fetchMedia,
  } = useAdminResource('/admin/media', {
    enabled: activeTab === 'media',
    initialData: [],
    onError: () => {
      toast.error('Failed to load media');
    },
  });

  const {
    data: historyItems,
    refresh: fetchHistory,
  } = useAdminResource('/admin/history', {
    enabled: activeTab === 'history',
    initialData: [],
    onError: () => {
      toast.error('Failed to load history');
    },
  });

  // Get auth headers
  // Verify auth and get role
  const handleLogout = () => {
    clearAdminToken();
    navigate('/login');
    toast.success('Logged out successfully');
  };

  const openConfirm = (title, description, action) => {
    setConfirmState({ open: true, title, description, action });
  };

  useEffect(() => {
    const verifyAuth = async () => {
      try {
        const response = await api.post('/auth/verify', {}, adminAuthHeaders());
        setUserRole(response.data.role || 'admin');
        setCurrentUsername(response.data.username);
      } catch (error) {
        console.error('Auth error:', error);
        clearAdminToken();
        navigate('/login');
        toast.success('Logged out successfully');
      }
    };
    verifyAuth();
  }, [navigate]);

  const tabs = [
    { value: 'services', label: 'Services', endpoint: '/services' },
    { value: 'tours', label: 'Tours' },
    { value: 'cases', label: 'Cases', endpoint: '/cases' },
    { value: 'events', label: 'Events', endpoint: '/events' },
    { value: 'projects', label: 'Projects', endpoint: '/projects' },
    { value: 'partners', label: 'Partners', endpoint: '/partners' },
    { value: 'articles', label: 'Articles', endpoint: '/articles' },
    { value: 'team', label: 'Team', endpoint: '/team' },
    { value: 'site_pages', label: 'Site Pages' },
    { value: 'navigation', label: 'Navigation & Footer' },
    { value: 'forms', label: 'Forms' },
    { value: 'submissions', label: 'Submissions' },
    { value: 'history', label: 'History' },
    { value: 'media', label: 'Media Library' },
    { value: 'theme', label: 'Theme' },
    { value: 'design', label: 'Design Controls' },
  ];

  // Add users tab for superadmin
  if (userRole === 'superadmin') {
    tabs.push({ value: 'users', label: 'Users', endpoint: '/admin/users', icon: Users });
  }

  useEffect(() => {
    setSearchQuery('');
    setSortMode('title-asc');
    if (activeTab === 'users') {
    } else if (activeTab === 'tours') {
      setEditingTour(null);
    } else if (activeTab === 'site_pages') {
      setEditingPage(null);
      setIsCreatingPage(false);
    } else if (activeTab === 'navigation' || activeTab === 'theme' || activeTab === 'design') {
    } else if (activeTab === 'forms') {
      setEditingForm(null);
      setIsCreatingForm(false);
    } else if (activeTab === 'submissions') {
      fetchSubmissions();
    } else {
      fetchData();
    }
    // Fetch handlers are intentionally scoped in this component until the remaining Admin split is complete.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const filteredData = [...data]
    .filter((item) => {
      const q = searchQuery.trim().toLowerCase();
      if (!q) return true;
      const haystack = [
        item.name,
        item.title,
        item.slug,
        item.description,
        item.excerpt,
        item.client,
        item.category,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    })
    .sort((a, b) => {
      const getLabel = (item) => (item.name || item.title || item.slug || '').toString().toLowerCase();
      const left = getLabel(a);
      const right = getLabel(b);
      if (sortMode === 'title-desc') return right.localeCompare(left);
      return left.localeCompare(right);
    });

  const filteredDynamicPages = [...dynamicPages]
    .filter((page) => {
      const q = searchQuery.trim().toLowerCase();
      if (!q) return true;
      return `${page.title || ''} ${page.slug || ''}`.toLowerCase().includes(q);
    })
    .sort((a, b) => {
      const left = (a.title || a.slug || '').toLowerCase();
      const right = (b.title || b.slug || '').toLowerCase();
      return sortMode === 'title-desc' ? right.localeCompare(left) : left.localeCompare(right);
    });

  const filteredForms = [...forms]
    .filter((form) => {
      const q = searchQuery.trim().toLowerCase();
      if (!q) return true;
      return `${form.title || ''} ${form.slug || ''}`.toLowerCase().includes(q);
    })
    .sort((a, b) => {
      const left = (a.title || a.slug || '').toLowerCase();
      const right = (b.title || b.slug || '').toLowerCase();
      return sortMode === 'title-desc' ? right.localeCompare(left) : left.localeCompare(right);
    });

  const TOUR_SLUGS = ['tours', 'tours-travel', 'tours-business', 'tours-retreat', 'tours-production'];
  const TOUR_LABELS = {
    'tours': '🗺 Туры (обзор)',
    'tours-travel': '🧭 Туристический тур',
    'tours-business': '💼 Бизнес-тур',
    'tours-retreat': '🧘 Ретрит-тур',
    'tours-production': '🎥 Продакшн-тур',
  };

  const tourPages = [...dynamicPages]
    .filter((page) => TOUR_SLUGS.includes(page.slug))
    .sort((a, b) => TOUR_SLUGS.indexOf(a.slug) - TOUR_SLUGS.indexOf(b.slug));

  const fetchData = async () => {
    try {
      const currentTab = tabs.find(t => t.value === activeTab);
      if (!currentTab) return;
      const response = await api.get(currentTab.endpoint);
      setData(response.data);
      setEditingItem(null);
      setIsCreating(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Ошибка загрузки данных');
    }
  };

  const saveSiteSettings = async (settings) => {
    try {
      await api.put('/admin/settings', settings, adminAuthHeaders());
      toast.success('Settings saved');
      setSiteSettings(settings);
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    }
  };

  const handleEditTour = (page) => {
    setEditingTour({ ...page, hide_title: Boolean(page.hide_title), full_width: Boolean(page.full_width), show_hero: Boolean(page.show_hero) });
  };

  const handleSaveTour = async () => {
    try {
      const payload = {
        ...editingTour,
        hide_title: Boolean(editingTour.hide_title),
        full_width: Boolean(editingTour.full_width),
        show_hero: Boolean(editingTour.show_hero),
      };
      await api.put(`/admin/pages-dynamic/${editingTour.id}`, payload, adminAuthHeaders());
      toast.success('Tour page updated');
      fetchDynamicPages();
      setEditingTour(null);
    } catch (error) {
      console.error('Error saving tour:', error);
      toast.error('Failed to save tour page');
    }
  };

  const fetchSubmissions = async (formId = selectedFormId) => {
    try {
      const query = formId && formId !== 'all' ? `?form_id=${formId}` : '';
      const response = await api.get(`/admin/submissions${query}`, adminAuthHeaders());
      setSubmissions(response.data);
    } catch (error) {
      console.error('Error fetching submissions:', error);
      toast.error('Failed to load submissions');
    }
  };


  const handleCreate = () => {
    setIsCreating(true);
    setEditingItem(getEmptyItem(activeTab));
  };

  const handleEdit = (item) => {
    setEditingItem({ ...item });
    setIsCreating(false);
  };

  const handleSave = async () => {
    const validationError = validateContentItem(editingItem, activeTab);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    try {
      if (isCreating) {
        editingItem.id = Date.now().toString();
        await api.post(`/admin/${activeTab}`, editingItem, adminAuthHeaders());
        toast.success('Создано успешно!');
      } else {
        await api.put(`/admin/${activeTab}/${editingItem.id}`, editingItem, adminAuthHeaders());
        toast.success('Обновлено успешно!');
      }
      fetchData();
    } catch (error) {
      console.error('Error saving:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again');
        handleLogout();
      } else {
        toast.error('Ошибка сохранения');
      }
    }
  };

  const handleDelete = async (id, confirmed = false) => {
    if (!confirmed) {
      openConfirm('Удалить элемент?', 'Это действие нельзя отменить.', () => handleDelete(id, true));
      return;
    }
    try {
      await api.delete(`/admin/${activeTab}/${id}`, adminAuthHeaders());
      toast.success('Удалено успешно!');
      fetchData();
    } catch (error) {
      console.error('Error deleting:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again');
        handleLogout();
      } else {
        toast.error('Ошибка удаления');
      }
    }
  };

  // Password change handlers
  const handleChangePassword = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Пароли не совпадают');
      return;
    }
    if (passwordData.new_password.length < 6) {
      toast.error('Пароль должен быть не менее 6 символов');
      return;
    }
    try {
      await api.post('/auth/change-password', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
      }, adminAuthHeaders());
      toast.success('Пароль успешно изменен!');
      setShowPasswordModal(false);
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      console.error('Error changing password:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Ошибка смены пароля');
      }
    }
  };

  // User management handlers
  const handleCreateUser = async () => {
    if (!newUserData.username || !newUserData.password) {
      toast.error('Заполните все поля');
      return;
    }
    if (newUserData.password.length < 6) {
      toast.error('Пароль должен быть не менее 6 символов');
      return;
    }
    try {
      await api.post('/admin/users', newUserData, adminAuthHeaders());
      toast.success('Пользователь создан!');
      setNewUserData({ username: '', password: '', role: 'admin' });
      fetchUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Ошибка создания пользователя');
      }
    }
  };

  const handleUpdateUser = async (userId) => {
    try {
      await api.put(`/admin/users/${userId}`, editingUser, adminAuthHeaders());
      toast.success('Пользователь обновлен!');
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(error.response?.data?.detail || 'Ошибка обновления');
    }
  };

  const handleResetPassword = async (userId) => {
    if (resetPasswordValue.length < 6) {
      toast.error('Пароль должен быть не менее 6 символов');
      return;
    }
    try {
      await api.put(`/admin/users/${userId}/password`, { new_password: resetPasswordValue }, adminAuthHeaders());
      toast.success('Пароль сброшен!');
      setResetPasswordUserId(null);
      setResetPasswordValue('');
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error('Ошибка сброса пароля');
    }
  };

  const handleDeleteUser = async (userId, confirmed = false) => {
    if (!confirmed) {
      openConfirm('Удалить пользователя?', 'Пользователь потеряет доступ к админке.', () => handleDeleteUser(userId, true));
      return;
    }
    try {
      await api.delete(`/admin/users/${userId}`, adminAuthHeaders());
      toast.success('Пользователь удален!');
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'Ошибка удаления');
    }
  };

  const handleCreatePage = () => {
    setIsCreatingPage(true);
    setEditingPage(getEmptyDynamicPage());
  };

  const handleEditPage = (page) => {
    setEditingPage({ ...page, hide_title: Boolean(page.hide_title), full_width: Boolean(page.full_width) });
    setIsCreatingPage(false);
  };

  const handleSavePage = async () => {
    const validationError = validateDynamicPage(editingPage);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    try {
      if (isCreatingPage) {
        const payload = { ...editingPage, id: Date.now().toString(), hide_title: Boolean(editingPage.hide_title), full_width: Boolean(editingPage.full_width) };
        await api.post('/admin/pages-dynamic', payload, adminAuthHeaders());
        toast.success('Page created');
      } else {
        await api.put(`/admin/pages-dynamic/${editingPage.id}`, { ...editingPage, hide_title: Boolean(editingPage.hide_title), full_width: Boolean(editingPage.full_width) }, adminAuthHeaders());
        toast.success('Page updated');
      }
      fetchDynamicPages();
    } catch (error) {
      console.error('Error saving page:', error);
      toast.error('Failed to save page');
    }
  };

  const handleDeletePage = async (pageId, confirmed = false) => {
    if (!confirmed) {
      openConfirm('Delete page?', 'This dynamic page will be removed permanently.', () => handleDeletePage(pageId, true));
      return;
    }
    try {
      await api.delete(`/admin/pages-dynamic/${pageId}`, adminAuthHeaders());
      toast.success('Page deleted');
      fetchDynamicPages();
    } catch (error) {
      console.error('Error deleting page:', error);
      toast.error('Failed to delete page');
    }
  };

  const handleCreateForm = () => {
    setIsCreatingForm(true);
    setEditingForm(getEmptyForm());
  };

  const handleEditForm = (form) => {
    setEditingForm({ ...form });
    setIsCreatingForm(false);
  };

  const handleSaveForm = async () => {
    const validationError = validateFormDefinition(editingForm);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    try {
      if (isCreatingForm) {
        const payload = { ...editingForm, id: Date.now().toString() };
        await api.post('/admin/forms', payload, adminAuthHeaders());
        toast.success('Form created');
      } else {
        await api.put(`/admin/forms/${editingForm.id}`, editingForm, adminAuthHeaders());
        toast.success('Form updated');
      }
      fetchForms();
    } catch (error) {
      console.error('Error saving form:', error);
      toast.error('Failed to save form');
    }
  };

  const handleDeleteForm = async (formId, confirmed = false) => {
    if (!confirmed) {
      openConfirm('Delete form?', 'The form definition will be removed permanently.', () => handleDeleteForm(formId, true));
      return;
    }
    try {
      await api.delete(`/admin/forms/${formId}`, adminAuthHeaders());
      toast.success('Form deleted');
      fetchForms();
    } catch (error) {
      console.error('Error deleting form:', error);
      toast.error('Failed to delete form');
    }
  };

  const handleUploadMedia = async () => {
    if (!mediaFile) return;
    try {
      const formData = new FormData();
      formData.append('file', mediaFile);
      await api.post('/admin/media', formData, adminMultipartHeaders());
      toast.success('Media uploaded');
      setMediaFile(null);
      fetchMedia();
    } catch (error) {
      console.error('Error uploading media:', error);
      toast.error('Failed to upload media');
    }
  };

  const handleDeleteMedia = async (mediaId, confirmed = false) => {
    if (!confirmed) {
      openConfirm('Delete media file?', 'The file metadata and uploaded asset will be removed permanently.', () => handleDeleteMedia(mediaId, true));
      return;
    }
    try {
      await api.delete(`/admin/media/${mediaId}`, adminAuthHeaders());
      toast.success('Media deleted');
      fetchMedia();
    } catch (error) {
      console.error('Error deleting media:', error);
      toast.error('Failed to delete media');
    }
  };

  const handleCopyUrl = async (url) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = url;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const success = document.execCommand('copy');
        document.body.removeChild(textArea);
        if (!success) {
          throw new Error('execCommand failed');
        }
      }
      toast.success('URL copied');
    } catch (error) {
      console.error('Clipboard error:', error);
      toast.error('Failed to copy URL');
    }
  };

  const getEmptyDynamicPage = () => ({
    id: '',
    slug: '',
    title: '',
    title_en: '',
    blocks: [],
    hide_title: false,
    full_width: false,
    publication_status: 'draft',
  });

  const getEmptyForm = () => ({
    id: '',
    slug: '',
    title: '',
    title_en: '',
    submit_message: '',
    submit_message_en: '',
    fields: [],
    publication_status: 'draft',
  });

  const getEmptyField = () => ({
    id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    type: 'text',
    label: '',
    label_en: '',
    required: false,
    options: [],
    options_en: [],
  });

  const getEmptyBlock = (type) => {
    const base = { id: `${Date.now()}-${Math.random().toString(16).slice(2)}`, type };
    switch (type) {
      case 'hero':
        return {
          ...base,
          title: '',
          title_en: '',
          title_size_mobile: '',
          title_size_desktop: '',
          title_font_style: 'normal',
          title_font_weight: '900',
          title_text_transform: 'none',
          subtitle: '',
          subtitle_en: '',
          subtitle_size_mobile: '',
          subtitle_size_desktop: '',
          subtitle_font_style: 'normal',
          subtitle_font_weight: '400',
          subtitle_text_transform: 'none',
          cta_label: '',
          cta_label_en: '',
          cta_href: '',
          background_image: '',
          background_video: '',
          use_liquid: false,
          liquid_colors: [],
          liquid_settings: {
            mouseForce: 20,
            cursorSize: 100,
            resolution: 0.5,
            dt: 0.014,
            BFECC: true,
            isViscous: false,
            viscous: 30,
            iterationsViscous: 32,
            iterationsPoisson: 32,
            isBounce: false,
            autoDemo: true,
            autoSpeed: 0.5,
            autoIntensity: 2.2,
            takeoverDuration: 0.25,
            autoResumeDelay: 1000,
            autoRampDuration: 0.6,
          },
          glitch_enabled: true,
          glitch_speed: 1,
          glitch_shadows: true,
          glitch_on_hover: false,
          full_bleed: false,
        };
      case 'text':
        return { ...base, heading: '', heading_en: '', body: '', body_en: '' };
      case 'image':
        return { ...base, url: '', caption: '', caption_en: '' };
      case 'gallery':
        return { ...base, images: [] };
      case 'video':
        return { ...base, source: 'youtube', url: '', title: '', title_en: '' };
      case 'form':
        return { ...base, form_slug: '' };
      case 'cards':
        return { ...base, title: '', title_en: '', items: [] };
      case 'stats':
        return {
          ...base,
          items: [],
          countup_enabled: true,
          countup_duration: 1,
          countup_delay: 0,
          countup_direction: 'up',
          countup_separator: '',
          countup_start_when: true,
          gradient_enabled: true,
          gradient_from: '#E11D2E',
          gradient_to: '#FFFFFF',
          gradient_angle: 90,
        };
      case 'logo_grid':
        return { ...base, items: [] };
      case 'cta':
        return { ...base, title: '', title_en: '', body: '', body_en: '', button_label: '', button_label_en: '', button_href: '', background_image: '' };
      case 'list':
        return { ...base, title: '', title_en: '', items: [], items_en: [] };
      case 'collection':
        return { ...base, title: '', title_en: '', collection: 'services', limit: 0, detail_prefix: '' };
      case 'html':
        return { ...base, html: '', html_en: '' };
      case 'marquee':
        return { ...base, items: [] };
      case 'spacer':
        return { ...base, size: 32 };
      default:
        return base;
    }
  };

  const getEmptyItem = (type) => {
    const base = { id: '', name: '', description: '', image_url: '', publication_status: 'draft' };
    switch (type) {
      case 'services':
        return { ...base, slug: '', features: [] };
      case 'cases':
        return { ...base, slug: '', title: '', client: '', category: '', challenge: '', solution: '', results: [], created_at: new Date().toISOString() };
      case 'events':
        return { ...base, slug: '', title: '', date: '', location: '', type: '', program: [] };
      case 'projects':
        return { ...base, slug: '', title: '', stage: '', industry: '', country: '', capital_required: '', timeline: '', status: 'active' };
      case 'partners':
        return { ...base, slug: '', categories: [], country: '', logo_url: '' };
      case 'articles':
        return { ...base, slug: '', title: '', excerpt: '', content: '', author: '', published_at: new Date().toISOString(), category: '' };
      case 'team':
        return { ...base, position: '', bio: '', linkedin: '' };
      case 'pages':
        return { id: '', slug: '', title: '', content: '', updated_at: new Date().toISOString(), publication_status: 'draft' };
      default:
        return base;
    }
  };

  const workflowCollections = {
    site_pages: dynamicPages,
    forms,
    tours: tourPages,
    services: data,
    cases: data,
    events: data,
    projects: data,
    partners: data,
    articles: data,
    team: data,
    pages: data,
  };

  const workflowItems = Array.isArray(workflowCollections[activeTab]) ? workflowCollections[activeTab] : [];
  const workflowDraftCount = workflowItems.filter((item) => (item.publication_status || 'published') === 'draft').length;
  const workflowPublishedCount = workflowItems.filter((item) => (item.publication_status || 'published') === 'published').length;
  const showWorkflowPanel = !['users', 'history', 'media'].includes(activeTab);

  const handleWorkflowCreate = () => {
    if (activeTab === 'site_pages' || activeTab === 'tours') {
      handleCreatePage();
      return;
    }
    if (activeTab === 'forms') {
      handleCreateForm();
      return;
    }
    if (tabs.some((tab) => tab.value === activeTab && tab.endpoint)) {
      handleCreate();
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-24" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8 gap-4">
            <div>
              <h1 className="text-4xl md:text-6xl font-black font-heading text-white tracking-tighter uppercase">
                АДМИН-ПАНЕЛЬ
              </h1>
              <p className="text-white/70 mt-2">
                {currentUsername} ({userRole === 'superadmin' ? 'Суперадмин' : 'Админ'})
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={() => setShowPasswordModal(true)}
                className="bg-white/10 hover:bg-white/20 text-white rounded-full"
                data-testid="change-password-button"
              >
                <Key className="w-5 h-5 mr-2" />
                Сменить пароль
              </Button>
              <Button
                onClick={handleLogout}
                className="bg-white/10 hover:bg-white/20 text-white rounded-full"
                data-testid="logout-button"
              >
                <LogOut className="w-5 h-5 mr-2" />
                Выйти
              </Button>
            </div>
          </div>
        </motion.div>

        {showWorkflowPanel && (
          <AdminWorkflowPanel
            title={tabs.find((tab) => tab.value === activeTab)?.label || 'Content'}
            draftCount={workflowDraftCount}
            publishedCount={workflowPublishedCount}
            submissionCount={submissions.length}
            onCreate={activeTab === 'submissions' || activeTab === 'navigation' || activeTab === 'theme' || activeTab === 'design' ? null : handleWorkflowCreate}
            onOpenHistory={() => setActiveTab('history')}
            onOpenSubmissions={() => setActiveTab('submissions')}
          />
        )}

        {/* Password Change Modal */}
        {showPasswordModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="glass rounded-3xl p-8 w-full max-w-md"
            >
              <h2 className="text-2xl font-bold text-white mb-6">Смена пароля</h2>
              <div className="space-y-4">
                <div>
                  <Label className="text-white">Текущий пароль</Label>
                  <Input
                    type="password"
                    value={passwordData.current_password}
                    onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                    className="bg-zinc-900/50 border-white/10 text-white"
                  />
                </div>
                <div>
                  <Label className="text-white">Новый пароль</Label>
                  <Input
                    type="password"
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                    className="bg-zinc-900/50 border-white/10 text-white"
                  />
                </div>
                <div>
                  <Label className="text-white">Подтвердите новый пароль</Label>
                  <Input
                    type="password"
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                    className="bg-zinc-900/50 border-white/10 text-white"
                  />
                </div>
                <div className="flex gap-3 mt-6">
                  <Button
                    onClick={() => {
                      setShowPasswordModal(false);
                      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
                    }}
                    className="flex-1 bg-white/10 hover:bg-white/20 text-white rounded-xl"
                  >
                    Отмена
                  </Button>
                  <Button
                    onClick={handleChangePassword}
                    className="flex-1 bg-[#E11D2E] hover:bg-[#E11D2E]/90 text-white rounded-xl"
                  >
                    Сохранить
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white/5 rounded-full p-1 flex-wrap h-auto gap-1">
            {tabs.map((tab) => (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                className="data-[state=active]:bg-[#E11D2E] data-[state=active]:text-white rounded-full px-4 py-2 text-white/70"
                data-testid={`tab-${tab.value}`}
              >
                {tab.icon && <tab.icon className="w-4 h-4 mr-2" />}
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Users Tab Content */}
          {activeTab === 'users' && userRole === 'superadmin' && (
            <div className="glass rounded-3xl p-6 md:p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white flex items-center">
                  <Users className="w-6 h-6 mr-3 text-[#E11D2E]" />
                  Управление пользователями
                </h2>
              </div>

              {/* Create new user form */}
              <div className="glass rounded-2xl p-6 mb-6">
                <h3 className="text-lg font-bold text-white mb-4">Добавить пользователя</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Input
                    placeholder="Логин"
                    value={newUserData.username}
                    onChange={(e) => setNewUserData({ ...newUserData, username: e.target.value })}
                    className="bg-zinc-900/50 border-white/10 text-white"
                  />
                  <Input
                    type="password"
                    placeholder="Пароль"
                    value={newUserData.password}
                    onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })}
                    className="bg-zinc-900/50 border-white/10 text-white"
                  />
                  <select
                    value={newUserData.role}
                    onChange={(e) => setNewUserData({ ...newUserData, role: e.target.value })}
                    className="bg-zinc-900/50 border border-white/10 text-white rounded-lg px-4"
                  >
                    <option value="admin">Админ</option>
                    <option value="superadmin">Суперадмин</option>
                  </select>
                  <Button
                    onClick={handleCreateUser}
                    className="bg-[#E11D2E] hover:bg-[#E11D2E]/90 text-white rounded-xl"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Создать
                  </Button>
                </div>
              </div>

              {/* Users list */}
              <div className="space-y-4">
                {users.map((user) => (
                  <div key={user.id} className="glass rounded-2xl p-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-[#E11D2E]/20 flex items-center justify-center">
                        {user.role === 'superadmin' ? (
                          <Shield className="w-6 h-6 text-[#E11D2E]" />
                        ) : (
                          <User className="w-6 h-6 text-white/60" />
                        )}
                      </div>
                      <div>
                        {editingUser?.id === user.id ? (
                          <div className="flex items-center gap-2">
                            <Input
                              value={editingUser.username}
                              onChange={(e) => setEditingUser({ ...editingUser, username: e.target.value })}
                              className="bg-zinc-900/50 border-white/10 text-white w-40"
                            />
                            <select
                              value={editingUser.role}
                              onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                              className="bg-zinc-900/50 border border-white/10 text-white rounded-lg px-3 py-2"
                            >
                              <option value="admin">Админ</option>
                              <option value="superadmin">Суперадмин</option>
                            </select>
                          </div>
                        ) : (
                          <>
                            <p className="text-white font-bold">{user.username}</p>
                            <p className="text-white/60 text-sm">
                              {user.role === 'superadmin' ? 'Суперадмин' : 'Админ'} • {new Date(user.created_at).toLocaleDateString('ru-RU')}
                            </p>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Reset password form */}
                    {resetPasswordUserId === user.id && (
                      <div className="flex items-center gap-2">
                        <Input
                          type="password"
                          placeholder="Новый пароль"
                          value={resetPasswordValue}
                          onChange={(e) => setResetPasswordValue(e.target.value)}
                          className="bg-zinc-900/50 border-white/10 text-white w-40"
                        />
                        <Button
                          onClick={() => handleResetPassword(user.id)}
                          className="bg-green-600 hover:bg-green-700 text-white rounded-lg px-3"
                        >
                          <Save className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={() => { setResetPasswordUserId(null); setResetPasswordValue(''); }}
                          className="bg-white/10 hover:bg-white/20 text-white rounded-lg px-3"
                        >
                          ✕
                        </Button>
                      </div>
                    )}

                    <div className="flex items-center gap-2">
                      {editingUser?.id === user.id ? (
                        <>
                          <Button
                            onClick={() => handleUpdateUser(user.id)}
                            className="bg-green-600 hover:bg-green-700 text-white rounded-lg"
                          >
                            <Save className="w-4 h-4 mr-1" />
                            Сохранить
                          </Button>
                          <Button
                            onClick={() => setEditingUser(null)}
                            className="bg-white/10 hover:bg-white/20 text-white rounded-lg"
                          >
                            Отмена
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            onClick={() => setEditingUser({ ...user })}
                            className="bg-white/10 hover:bg-white/20 text-white rounded-lg"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            onClick={() => setResetPasswordUserId(user.id)}
                            className="bg-white/10 hover:bg-white/20 text-white rounded-lg"
                            title="Сбросить пароль"
                          >
                            <Key className="w-4 h-4" />
                          </Button>
                          <Button
                            onClick={() => handleDeleteUser(user.id)}
                            className="bg-red-600/20 hover:bg-red-600/40 text-red-400 rounded-lg"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <TabsContent value="tours" className="mt-6">
            <div className="glass rounded-3xl p-6 md:p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">Tours Management</h2>
                {editingTour && (
                  <Button onClick={() => setEditingTour(null)} className="bg-white/10 hover:bg-white/20 text-white rounded-full">
                    ← Back to list
                  </Button>
                )}
              </div>

              {editingTour ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-xl">{TOUR_LABELS[editingTour.slug] || editingTour.slug}</span>
                    <span className="text-white/40 text-sm">/{editingTour.slug}</span>
                  </div>
                  <DynamicPageEditor
                    page={editingTour}
                    onChange={setEditingTour}
                    onSave={handleSaveTour}
                    onCancel={() => setEditingTour(null)}
                    createBlock={getEmptyBlock}
                    showHeroToggle
                  />
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-white/50 text-sm mb-4">Edit content, images, texts, pricing and CTAs for each tour page.</p>
                  {tourPages.map((page) => (
                    <div key={page.id} className="glass rounded-2xl p-5 flex items-center justify-between hover:bg-white/5 transition-colors cursor-pointer" onClick={() => handleEditTour(page)}>
                      <div className="flex-1">
                        <h3 className="text-white font-bold text-lg">{TOUR_LABELS[page.slug] || page.title}</h3>
                        <p className="text-white/50 text-sm">/{page.slug === 'tours' ? 'tours' : `tours/${page.slug.replace('tours-', '')}`} &middot; {(page.blocks || []).length} blocks</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <a href={page.slug === 'tours' ? '/tours' : `/tours/${page.slug.replace('tours-', '')}`} target="_blank" rel="noopener noreferrer" className="text-white/40 hover:text-white text-sm" onClick={(e) => e.stopPropagation()}>
                          Preview ↗
                        </a>
                        <Button onClick={(e) => { e.stopPropagation(); handleEditTour(page); }} className="bg-white/10 hover:bg-white/20 text-white rounded-lg">
                          <Edit className="w-4 h-4 mr-2" />
                          Edit
                        </Button>
                      </div>
                    </div>
                  ))}
                  {tourPages.length === 0 && (
                    <p className="text-white/40 text-center py-12">No tour pages found. Run the init_tours.py script first.</p>
                  )}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="site_pages" className="mt-6">
            <div className="glass rounded-3xl p-6 md:p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">Site Pages</h2>
                <div className="flex flex-col md:flex-row gap-3">
                  <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="bg-zinc-900/50 border-white/10 text-white min-w-[220px]" placeholder="Search pages" />
                  <select value={sortMode} onChange={(e) => setSortMode(e.target.value)} className="bg-zinc-900/50 border border-white/10 text-white rounded-lg px-4 py-2">
                    <option value="title-asc">Title A-Z</option>
                    <option value="title-desc">Title Z-A</option>
                  </select>
                  <Button onClick={handleCreatePage} className="bg-[#E11D2E] hover:bg-[#E11D2E]/90 text-white rounded-full">
                    <Plus className="w-5 h-5 mr-2" />
                    New Page
                  </Button>
                </div>
              </div>

              {(editingPage || isCreatingPage) && (
                <DynamicPageEditor
                  page={editingPage}
                  onChange={setEditingPage}
                  onSave={handleSavePage}
                  onCancel={() => { setEditingPage(null); setIsCreatingPage(false); }}
                  createBlock={getEmptyBlock}
                />
              )}

              {!editingPage && !isCreatingPage && (
                <div className="space-y-4">
                  {filteredDynamicPages.map((page) => (
                    <div key={page.id} className="glass rounded-2xl p-4 flex items-center justify-between">
                      <div className="flex-1">
                        <h3 className="text-white font-bold">{page.title}</h3>
                        <p className="text-white/60 text-sm">/{page.slug}</p>
                        <p className="text-white/40 text-xs uppercase tracking-widest mt-1">{page.publication_status || 'published'}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button onClick={() => handleEditPage(page)} className="bg-white/10 hover:bg-white/20 text-white rounded-lg">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button onClick={() => handleDeletePage(page.id)} className="bg-red-600/20 hover:bg-red-600/40 text-red-400 rounded-lg">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {filteredDynamicPages.length === 0 && (
                    <p className="text-white/40 text-center py-12">No pages yet.</p>
                  )}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="navigation" className="mt-6">
            <div className="glass rounded-3xl p-6 md:p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">Navigation & Footer</h2>
                <Button
                  onClick={() => saveSiteSettings(siteSettings)}
                  disabled={!siteSettings}
                  className="bg-[#E11D2E] hover:bg-[#E11D2E]/90 text-white rounded-full"
                >
                  <Save className="w-5 h-5 mr-2" />
                  Save Settings
                </Button>
              </div>
              {!siteSettings ? (
                <p className="text-white/60">Loading...</p>
              ) : (
                <SettingsEditor settings={siteSettings} setSettings={setSiteSettings} />
              )}
            </div>
          </TabsContent>

          <TabsContent value="forms" className="mt-6">
            <div className="glass rounded-3xl p-6 md:p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">Forms</h2>
                <div className="flex flex-col md:flex-row gap-3">
                  <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="bg-zinc-900/50 border-white/10 text-white min-w-[220px]" placeholder="Search forms" />
                  <select value={sortMode} onChange={(e) => setSortMode(e.target.value)} className="bg-zinc-900/50 border border-white/10 text-white rounded-lg px-4 py-2">
                    <option value="title-asc">Title A-Z</option>
                    <option value="title-desc">Title Z-A</option>
                  </select>
                  <Button
                    onClick={handleCreateForm}
                    className="bg-[#E11D2E] hover:bg-[#E11D2E]/90 text-white rounded-full"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    New Form
                  </Button>
                </div>
              </div>

              {(editingForm || isCreatingForm) && (
                <FormEditor
                  form={editingForm}
                  onChange={setEditingForm}
                  onSave={handleSaveForm}
                  onCancel={() => { setEditingForm(null); setIsCreatingForm(false); }}
                  createField={getEmptyField}
                />
              )}

              {!editingForm && !isCreatingForm && (
                <div className="space-y-4">
                  {filteredForms.map((form) => (
                    <div key={form.id} className="glass rounded-2xl p-4 flex items-center justify-between">
                      <div className="flex-1">
                        <h3 className="text-white font-bold">{form.title}</h3>
                        <p className="text-white/60 text-sm">/{form.slug}</p>
                        <p className="text-white/40 text-xs uppercase tracking-widest mt-1">{form.publication_status || 'published'}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button onClick={() => handleEditForm(form)} className="bg-white/10 hover:bg-white/20 text-white rounded-lg">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button onClick={() => handleDeleteForm(form.id)} className="bg-red-600/20 hover:bg-red-600/40 text-red-400 rounded-lg">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {filteredForms.length === 0 && (
                    <p className="text-white/40 text-center py-12">No forms yet.</p>
                  )}
                </div>
              )}
            </div>
          </TabsContent>
          <TabsContent value="submissions" className="mt-6">
            <div className="glass rounded-3xl p-6 md:p-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                <h2 className="text-2xl font-bold text-white">Submissions</h2>
                <div className="flex flex-col md:flex-row items-start md:items-center gap-3">
                  <select
                    value={selectedFormId}
                    onChange={(e) => {
                      setSelectedFormId(e.target.value);
                      fetchSubmissions(e.target.value);
                    }}
                    className="bg-zinc-900/50 border border-white/10 text-white rounded-lg px-4 py-2"
                  >
                    <option value="all">All forms</option>
                    {forms.map((form) => (
                      <option key={form.id} value={form.id}>{form.title}</option>
                    ))}
                  </select>
                  <Button onClick={() => fetchSubmissions()} className="bg-white/10 hover:bg-white/20 text-white rounded-xl">
                    Refresh
                  </Button>
                </div>
              </div>

              <div className="space-y-4">
                {submissions.map((submission) => {
                  const form = forms.find((f) => f.id === submission.form_id);
                  return (
                    <div key={submission.id} className="glass rounded-2xl p-4 space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-white font-bold">{form?.title || 'Form'}</p>
                        <p className="text-white/50 text-sm">{new Date(submission.created_at).toLocaleString()}</p>
                      </div>
                      <div className="text-white/70 text-sm grid grid-cols-1 md:grid-cols-2 gap-2">
                        {Object.entries(submission.payload || {}).map(([key, value]) => (
                          <div key={key} className="bg-black/30 rounded-lg px-3 py-2">
                            <span className="text-white/50">{key}:</span> {String(value)}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
                {submissions.length === 0 && (
                  <p className="text-white/40 text-center py-12">No submissions yet.</p>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="history" className="mt-6">
            <div className="glass rounded-3xl p-6 md:p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">Content History</h2>
                <Button onClick={fetchHistory} className="bg-white/10 hover:bg-white/20 text-white rounded-xl">
                  Refresh
                </Button>
              </div>

              <div className="space-y-4">
                {historyItems.map((entry) => (
                  <div key={entry.id} className="glass rounded-2xl p-4 space-y-2">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="text-xs uppercase tracking-widest text-[#E11D2E]">{entry.collection}</span>
                        <span className="text-white font-semibold">{entry.action}</span>
                        <span className="text-white/40 text-sm">{entry.document_id}</span>
                      </div>
                      <div className="text-white/40 text-sm">
                        {entry.actor?.username || 'system'} • {new Date(entry.created_at).toLocaleString('ru-RU')}
                      </div>
                    </div>
                    <div className="text-white/60 text-sm">
                      Publication status: {entry.after?.publication_status || entry.before?.publication_status || 'published'}
                    </div>
                  </div>
                ))}
                {historyItems.length === 0 && (
                  <p className="text-white/40 text-center py-12">No history yet.</p>
                )}
              </div>
            </div>
          </TabsContent>


          <TabsContent value="media" className="mt-6">
            <div className="glass rounded-3xl p-6 md:p-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                <h2 className="text-2xl font-bold text-white">Media Library</h2>
                <div className="flex flex-col md:flex-row items-start md:items-center gap-3">
                  <Input type="file" onChange={(e) => setMediaFile(e.target.files?.[0] || null)} className="bg-zinc-900/50 border-white/10 text-white" />
                  <Button onClick={handleUploadMedia} className="bg-[#E11D2E] hover:bg-[#E11D2E]/90 text-white rounded-xl">
                    Upload
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {mediaItems.map((media) => {
                  const rawUrl = media.url || '';
                  const baseUrl = BACKEND_URL || window.location.origin;
                  const url = rawUrl.startsWith('http') ? rawUrl : `${baseUrl}${rawUrl}`;
                  const isImage = media.content_type?.startsWith('image');
                  return (
                    <div key={media.id} className="glass rounded-2xl p-4 space-y-3">
                      <div className="rounded-xl overflow-hidden bg-black/40">
                        {isImage ? (
                          <img src={url} alt={media.original_name || ''} className="w-full h-40 object-cover" />
                        ) : (
                          <video src={url} className="w-full h-40 object-cover" />
                        )}
                      </div>
                      <div className="text-white/70 text-sm truncate">{media.original_name || media.filename}</div>
                      <div className="flex items-center gap-2">
                        <Button onClick={() => handleCopyUrl(url)} className="bg-white/10 hover:bg-white/20 text-white rounded-lg">Copy URL</Button>
                        <Button onClick={() => handleDeleteMedia(media.id)} className="bg-red-600/20 hover:bg-red-600/40 text-red-400 rounded-lg">Delete</Button>
                      </div>
                    </div>
                  );
                })}
                {mediaItems.length === 0 && (
                  <p className="text-white/40 text-center col-span-full">No media yet.</p>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="theme" className="mt-6">
            <div className="glass rounded-3xl p-6 md:p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">Theme</h2>
                <Button
                  onClick={() => saveSiteSettings(siteSettings)}
                  disabled={!siteSettings}
                  className="bg-[#E11D2E] hover:bg-[#E11D2E]/90 text-white rounded-full"
                >
                  <Save className="w-5 h-5 mr-2" />
                  Save Theme
                </Button>
              </div>
              {!siteSettings ? (
                <p className="text-white/60">Loading...</p>
              ) : (
                <ThemeEditor settings={siteSettings} setSettings={setSiteSettings} />
              )}
            </div>
          </TabsContent>

          <TabsContent value="design" className="mt-6">
            <div className="glass rounded-3xl p-6 md:p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">Design Controls</h2>
                <Button
                  onClick={() => saveSiteSettings(siteSettings)}
                  disabled={!siteSettings}
                  className="bg-[#E11D2E] hover:bg-[#E11D2E]/90 text-white rounded-full"
                >
                  <Save className="w-5 h-5 mr-2" />
                  Save Design
                </Button>
              </div>
              {!siteSettings ? (
                <p className="text-white/60">Loading...</p>
              ) : (
                <DesignControlsEditor settings={siteSettings} setSettings={setSiteSettings} />
              )}
            </div>
          </TabsContent>

          {/* Content Tabs */}
          {tabs.filter(t => t.value !== 'users' && t.endpoint).map((tab) => (
            <TabsContent key={tab.value} value={tab.value} className="mt-6">
              <div className="glass rounded-3xl p-6 md:p-8">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-white">{tab.label}</h2>
                  <div className="flex flex-col md:flex-row gap-3">
                    <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="bg-zinc-900/50 border-white/10 text-white min-w-[220px]" placeholder={`Search ${tab.label.toLowerCase()}`} />
                    <select value={sortMode} onChange={(e) => setSortMode(e.target.value)} className="bg-zinc-900/50 border border-white/10 text-white rounded-lg px-4 py-2">
                      <option value="title-asc">Title A-Z</option>
                      <option value="title-desc">Title Z-A</option>
                    </select>
                    <Button
                      onClick={handleCreate}
                      className="bg-[#E11D2E] hover:bg-[#E11D2E]/90 text-white rounded-full"
                      data-testid="create-button"
                    >
                      <Plus className="w-5 h-5 mr-2" />
                      Создать
                    </Button>
                  </div>
                </div>

                {/* Edit Form */}
                {(editingItem || isCreating) && (
                  <EditForm
                    item={editingItem}
                    type={activeTab}
                    onSave={handleSave}
                    onCancel={() => { setEditingItem(null); setIsCreating(false); }}
                    onChange={(field, value) => setEditingItem({ ...editingItem, [field]: value })}
                  />
                )}

                {/* Items List */}
                {!editingItem && !isCreating && (
                  <div className="space-y-4">
                    {filteredData.map((item, idx) => (
                      <div
                        key={item.id}
                        className="glass rounded-2xl p-4 flex items-center justify-between"
                      >
                        <div className="flex-1">
                          <h3 className="text-white font-bold">{item.name || item.title || item.slug}</h3>
                          <p className="text-white/40 text-xs uppercase tracking-widest mt-1">
                            {item.publication_status || 'published'}
                          </p>
                          <p className="text-white/60 text-sm truncate max-w-md">
                            {item.description || item.excerpt || item.content?.substring(0, 100)}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <Button
                            onClick={() => handleEdit(item)}
                            className="bg-white/10 hover:bg-white/20 text-white rounded-lg"
                            data-testid={`edit-button-${idx}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            onClick={() => handleDelete(item.id)}
                            className="bg-red-600/20 hover:bg-red-600/40 text-red-400 rounded-lg"
                            data-testid={`delete-button-${idx}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    {filteredData.length === 0 && (
                      <p className="text-white/40 text-center py-12">Нет данных</p>
                    )}
                  </div>
                )}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>

      <AlertDialog open={confirmState.open} onOpenChange={(open) => setConfirmState((prev) => ({ ...prev, open }))}>
        <AlertDialogContent className="border-white/10 bg-zinc-950 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmState.title}</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              {confirmState.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="border-white/10 bg-white/5 text-white hover:bg-white/10"
              onClick={() => setConfirmState({ open: false, title: '', description: '', action: null })}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-[#E11D2E] text-white hover:bg-[#E11D2E]/90"
              onClick={() => {
                const action = confirmState.action;
                setConfirmState({ open: false, title: '', description: '', action: null });
                if (action) action();
              }}
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
